import { useState, useEffect, useMemo, useRef } from "react";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { z } from "zod";
import { Check, CalendarIcon, AlertCircle, ChevronsUpDown, ArrowRight, ArrowRightLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, parse, isValid } from "date-fns";
import { parseLocalDate, todayISO } from "@/lib/dates";

const transferSchema = z.object({
  amount: z.number().positive({ message: "Amount must be greater than 0" }).max(1000000000, { message: "Amount too large" }),
  fromAccount: z.string().trim().min(1, { message: "Source account is required" }).max(100),
  toAccount: z.string().trim().min(1, { message: "Destination account is required" }).max(100),
  description: z.string().max(500, { message: "Description must be less than 500 characters" }).optional().nullable(),
  date: z.string().min(1, { message: "Date is required" }),
}).refine(data => data.fromAccount !== data.toAccount, {
  message: "Source and destination accounts must be different",
  path: ["toAccount"],
});

interface TransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  onSwitchToTransaction?: () => void;
}

export const TransferDialog = ({
  open,
  onOpenChange,
  onSuccess,
  onSwitchToTransaction,
}: TransferDialogProps) => {
  const [amount, setAmount] = useState("");
  const [fromAccount, setFromAccount] = useState("");
  const [fromAccountFocused, setFromAccountFocused] = useState(false);
  const [toAccount, setToAccount] = useState("");
  const [toAccountFocused, setToAccountFocused] = useState(false);
  const [description, setDescription] = useState("");
  const [dateInput, setDateInput] = useState("");
  const [date, setDate] = useState(todayISO());
  const [dateCalendarOpen, setDateCalendarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentSources, setPaymentSources] = useState<{ value: string; label: string; isWallet?: boolean; parentAccount?: string; id: string }[]>([]);
  const [onboardingDate, setOnboardingDate] = useState<string | null>(null);
  const { toast } = useToast();
  const { parseNumber, decimalSeparator, getPlaceholder, preferences } = useUserPreferences();

  const fromInputRef = useRef<HTMLInputElement>(null);
  const toInputRef = useRef<HTMLInputElement>(null);

  const getDateFormat = () => {
    const locale = preferences?.locale || "en-US";
    if (locale.startsWith("en-US")) return "MM/dd/yyyy";
    if (locale.startsWith("en-GB") || locale.startsWith("de") || locale.startsWith("fr") || locale.startsWith("es") || locale.startsWith("it")) return "dd/MM/yyyy";
    if (locale.startsWith("ja") || locale.startsWith("zh") || locale.startsWith("ko")) return "yyyy/MM/dd";
    return "dd/MM/yyyy";
  };

  const dateFormat = getDateFormat();

  const parseDateInput = (input: string): string | null => {
    if (!input.trim()) return null;
    try {
      const parsed = parse(input, dateFormat, new Date());
      if (isValid(parsed)) {
        return format(parsed, "yyyy-MM-dd");
      }
    } catch {
      try {
        const parsed = new Date(input);
        if (isValid(parsed)) {
          return format(parsed, "yyyy-MM-dd");
        }
      } catch {
        return null;
      }
    }
    return null;
  };

  const formatDateForDisplay = (isoDate: string): string => {
    try {
      const d = parseLocalDate(isoDate);
      if (isValid(d)) {
        return format(d, dateFormat);
      }
    } catch {
      // invalid date — fall back to the raw ISO string
    }
    return isoDate;
  };

  const filteredFromSources = useMemo(() => {
    if (!fromAccount.trim()) return paymentSources;
    const search = fromAccount.toLowerCase();
    return paymentSources.filter(source => 
      source.value.toLowerCase().includes(search) || 
      source.label.toLowerCase().includes(search)
    );
  }, [paymentSources, fromAccount]);

  const filteredToSources = useMemo(() => {
    const available = paymentSources.filter(s => s.value !== fromAccount);
    if (!toAccount.trim()) return available;
    const search = toAccount.toLowerCase();
    return available.filter(source => 
      source.value.toLowerCase().includes(search) || 
      source.label.toLowerCase().includes(search)
    );
  }, [paymentSources, toAccount, fromAccount]);

  const isFromAccountValid = useMemo(() => {
    if (!fromAccount.trim()) return false;
    return paymentSources.some(s => s.value === fromAccount);
  }, [paymentSources, fromAccount]);

  const isToAccountValid = useMemo(() => {
    if (!toAccount.trim()) return false;
    return paymentSources.some(s => s.value === toAccount) && toAccount !== fromAccount;
  }, [paymentSources, toAccount, fromAccount]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [accountsRes, walletsRes, prefsRes] = await Promise.all([
          supabase.from("accounts").select("id, name").order("created_at", { ascending: true }),
          supabase.from("wallets").select("id, account_id, name").order("created_at", { ascending: true }),
          supabase.from("user_preferences").select("onboarding_completed_at").single(),
        ]);

        if (accountsRes.error) throw accountsRes.error;
        
        const accounts = accountsRes.data || [];
        const wallets = walletsRes.data || [];
        
        const sources: { value: string; label: string; isWallet?: boolean; parentAccount?: string; id: string }[] = [];

        accounts.forEach(acc => {
          sources.push({ value: acc.name, label: acc.name, id: acc.id });

          const accountWallets = wallets.filter(w => w.account_id === acc.id);
          accountWallets.forEach(wallet => {
            sources.push({
              value: `${acc.name} → ${wallet.name}`,
              label: `${wallet.name}`,
              isWallet: true,
              parentAccount: acc.name,
              id: acc.id,
            });
          });
        });

        setPaymentSources(sources);
        
        if (sources.length > 0) {
          setFromAccount(sources[0].value);
        }

        if (prefsRes.data?.onboarding_completed_at) {
          setOnboardingDate(prefsRes.data.onboarding_completed_at);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (open) {
      fetchData();
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setAmount("");
    setFromAccount("");
    setToAccount("");
    setDateInput("");
    setDate(todayISO());
    setDescription("");
  };

  const isBackdated = () => {
    if (!onboardingDate) return false;
    const transactionDate = parseLocalDate(date);
    const onboardingDateObj = new Date(onboardingDate);
    transactionDate.setHours(0, 0, 0, 0);
    onboardingDateObj.setHours(0, 0, 0, 0);
    return transactionDate < onboardingDateObj;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const validatedData = transferSchema.parse({
        amount: parseNumber(amount),
        fromAccount,
        toAccount,
        description: description.trim() || null,
        date,
      });

      const backdated = isBackdated();

      const fromSource = paymentSources.find(s => s.value === validatedData.fromAccount);
      const toSource = paymentSources.find(s => s.value === validatedData.toAccount);
      if (!fromSource || !toSource) throw new Error("Could not resolve account IDs");

      // Create expense transaction from source account
      const { error: expenseError } = await supabase.from("transactions").insert({
        user_id: user.id,
        type: "expense",
        amount: validatedData.amount,
        category: "Transfer",
        account: validatedData.fromAccount,
        account_id: fromSource.id,
        description: `Transfer to ${validatedData.toAccount}${validatedData.description ? `: ${validatedData.description}` : ""}`,
        date: validatedData.date,
        is_backdated: backdated,
      });

      if (expenseError) throw expenseError;

      // Create income transaction to destination account
      const { error: incomeError } = await supabase.from("transactions").insert({
        user_id: user.id,
        type: "income",
        amount: validatedData.amount,
        category: "Transfer",
        account: validatedData.toAccount,
        account_id: toSource.id,
        description: `Transfer from ${validatedData.fromAccount}${validatedData.description ? `: ${validatedData.description}` : ""}`,
        date: validatedData.date,
        is_backdated: backdated,
      });

      if (incomeError) throw incomeError;

      toast({
        title: "Transfer Complete",
        description: `Transferred ${validatedData.amount} from ${validatedData.fromAccount} to ${validatedData.toAccount}`,
      });

      onOpenChange(false);
      onSuccess();
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: getErrorMessage(error),
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Transfer Between Accounts</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="transfer-amount">Amount</Label>
            <Input
              id="transfer-amount"
              type="text"
              inputMode="decimal"
              placeholder={getPlaceholder()}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              autoFocus
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Use "{decimalSeparator}" for decimals
            </p>
          </div>

          {/* From Account */}
          <div className="space-y-2">
            <Label>From Account</Label>
            <div className="flex gap-1">
              <div className="relative flex-1">
                <Input
                  ref={fromInputRef}
                  placeholder="Select source account..."
                  value={fromAccount}
                  onChange={(e) => setFromAccount(e.target.value)}
                  onFocus={() => setFromAccountFocused(true)}
                  onBlur={() => setTimeout(() => setFromAccountFocused(false), 150)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && filteredFromSources.length > 0) {
                      e.preventDefault();
                      setFromAccount(filteredFromSources[0].value);
                      setFromAccountFocused(false);
                    } else if (e.key === "Escape") {
                      setFromAccountFocused(false);
                    }
                  }}
                  className={cn("pr-8", fromAccount && !isFromAccountValid && "border-destructive")}
                />
                {fromAccount && isFromAccountValid && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-income-text" />
                )}
                {fromAccount && !isFromAccountValid && (
                  <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-destructive" />
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => {
                  setFromAccountFocused(!fromAccountFocused);
                  if (!fromAccountFocused) fromInputRef.current?.focus();
                }}
              >
                <ChevronsUpDown className="h-4 w-4" />
              </Button>
            </div>
            {fromAccountFocused && filteredFromSources.length > 0 && (
              <div className="rounded-md border bg-popover p-1 shadow-md max-h-[200px] overflow-y-auto">
                {filteredFromSources.map((source) => (
                  <button
                    key={source.value}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setFromAccount(source.value);
                      setFromAccountFocused(false);
                    }}
                    className={cn(
                      "flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-sm hover:bg-accent text-left",
                      source.isWallet && "pl-6",
                      fromAccount === source.value && "bg-accent"
                    )}
                  >
                    <Check className={cn("h-4 w-4", fromAccount === source.value ? "opacity-100" : "opacity-0")} />
                    {source.isWallet ? (
                      <span className="flex flex-col items-start">
                        <span>{source.label}</span>
                        <span className="text-xs text-muted-foreground">in {source.parentAccount}</span>
                      </span>
                    ) : (
                      <span className="font-medium">{source.label}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Arrow indicator */}
          <div className="flex justify-center">
            <ArrowRight className="h-6 w-6 text-muted-foreground" />
          </div>

          {/* To Account */}
          <div className="space-y-2">
            <Label>To Account</Label>
            <div className="flex gap-1">
              <div className="relative flex-1">
                <Input
                  ref={toInputRef}
                  placeholder="Select destination account..."
                  value={toAccount}
                  onChange={(e) => setToAccount(e.target.value)}
                  onFocus={() => setToAccountFocused(true)}
                  onBlur={() => setTimeout(() => setToAccountFocused(false), 150)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && filteredToSources.length > 0) {
                      e.preventDefault();
                      setToAccount(filteredToSources[0].value);
                      setToAccountFocused(false);
                    } else if (e.key === "Escape") {
                      setToAccountFocused(false);
                    }
                  }}
                  className={cn("pr-8", toAccount && !isToAccountValid && "border-destructive")}
                />
                {toAccount && isToAccountValid && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-income-text" />
                )}
                {toAccount && !isToAccountValid && (
                  <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-destructive" />
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => {
                  setToAccountFocused(!toAccountFocused);
                  if (!toAccountFocused) toInputRef.current?.focus();
                }}
              >
                <ChevronsUpDown className="h-4 w-4" />
              </Button>
            </div>
            {toAccountFocused && filteredToSources.length > 0 && (
              <div className="rounded-md border bg-popover p-1 shadow-md max-h-[200px] overflow-y-auto">
                {filteredToSources.map((source) => (
                  <button
                    key={source.value}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setToAccount(source.value);
                      setToAccountFocused(false);
                    }}
                    className={cn(
                      "flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-sm hover:bg-accent text-left",
                      source.isWallet && "pl-6",
                      toAccount === source.value && "bg-accent"
                    )}
                  >
                    <Check className={cn("h-4 w-4", toAccount === source.value ? "opacity-100" : "opacity-0")} />
                    {source.isWallet ? (
                      <span className="flex flex-col items-start">
                        <span>{source.label}</span>
                        <span className="text-xs text-muted-foreground">in {source.parentAccount}</span>
                      </span>
                    ) : (
                      <span className="font-medium">{source.label}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label>Date</Label>
            <div className="flex gap-1">
              <Input
                placeholder={dateFormat.toLowerCase()}
                value={dateInput}
                onChange={(e) => {
                  setDateInput(e.target.value);
                  const parsed = parseDateInput(e.target.value);
                  if (parsed) setDate(parsed);
                }}
                onBlur={() => {
                  if (!dateInput.trim()) {
                    setDateInput(formatDateForDisplay(date));
                  }
                }}
                className="flex-1"
              />
              <Popover open={dateCalendarOpen} onOpenChange={setDateCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" type="button">
                    <CalendarIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="single"
                    selected={date ? parseLocalDate(date) : undefined}
                    onSelect={(d) => {
                      if (d) {
                        const iso = format(d, "yyyy-MM-dd");
                        setDate(iso);
                        setDateInput(formatDateForDisplay(iso));
                      }
                      setDateCalendarOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="transfer-description">Description (optional)</Label>
            <Input
              id="transfer-description"
              placeholder="e.g., Moving savings"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !isFromAccountValid || !isToAccountValid || !amount}
              className="flex-1"
            >
              {loading ? "Transferring..." : "Transfer"}
            </Button>
          </div>

          {onSwitchToTransaction && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full text-muted-foreground hover:text-foreground gap-2"
              onClick={() => {
                onOpenChange(false);
                onSwitchToTransaction();
              }}
            >
              <ArrowRightLeft className="h-4 w-4" />
              Switch to Transaction
            </Button>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
